import { cursorPaging, type In, type ReportRun, type ReportScheduleInput } from "@clubcore/api";
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, type QueryClient } from "@tanstack/react-query";
import { useScope } from "../members/scope";
import { PAGE_SIZE, type RunSource } from "./types";

/** Everything cached here starts with the club slug, then "reports", so one invalidation reaches the lot. */
const ROOT = "reports";

function refreshSaved(queryClient: QueryClient, slug: string) {
  void queryClient.invalidateQueries({ queryKey: [slug, ROOT, "saved"] });
}

// ---------------------------------------------------------------- the catalog

/** Datasets the person may build on (`reports.build`). The list is short and fixed, so it is kept for a while. */
export function useDatasets(enabled = true) {
  const { api, slug } = useScope();
  return useQuery({ queryKey: [slug, ROOT, "datasets"], enabled, staleTime: 300_000, queryFn: ({ signal }) => api.reports.listDatasets(signal) });
}

export function useTemplates() {
  const { api, slug } = useScope();
  return useQuery({ queryKey: [slug, ROOT, "templates"], staleTime: 120_000, queryFn: ({ signal }) => api.reports.listTemplates(signal) });
}

/** The values a field takes, for a filter menu; waits until the person has opened the menu. */
export function useFieldValues(dataset: string, field: string, q: string, enabled: boolean) {
  const { api, slug } = useScope();
  return useQuery({
    queryKey: [slug, ROOT, "values", dataset, field, q],
    enabled: enabled && Boolean(dataset && field),
    staleTime: 60_000,
    queryFn: ({ signal }) => api.reports.fieldValues(dataset, field, q || undefined, signal),
  });
}

// ---------------------------------------------------------------- running

export interface RunPages {
  /** The first page's columns and the latest page's facts; undefined until the first page is in. */
  first: ReportRun | undefined;
  last: ReportRun | undefined;
  rows: unknown[][];
  query: ReturnType<typeof useRunQuery>;
}

function useRunQuery(source: RunSource | null, enabled: boolean, keepPrevious: boolean) {
  const { api, slug } = useScope();
  return useInfiniteQuery({
    queryKey: [slug, ROOT, "run", source],
    enabled: enabled && source !== null,
    staleTime: 15_000,
    initialPageParam: 0,
    queryFn: ({ pageParam, signal }) => api.reports.runReport({ ...source!, page: pageParam, page_size: PAGE_SIZE }, signal),
    getNextPageParam: (last) => (last.has_more ? last.page + 1 : undefined),
    retry: false,
    ...(keepPrevious ? { placeholderData: keepPreviousData } : {}),
  });
}

/**
 * Runs a report page by page. A new source cancels the request still in flight for the old one. Rows of every loaded page are
 * returned together; the facts that change page to page (`has_more`, `truncated`, total) come from the latest page.
 * `keepPrevious` keeps showing the last result while a changed source loads (the builder's preview), marked as stale.
 */
export function useReportRun(source: RunSource | null, enabled = true, keepPrevious = false): RunPages {
  const query = useRunQuery(source, enabled, keepPrevious);
  const pages = query.data?.pages ?? [];
  return { first: pages[0], last: pages.at(-1), rows: pages.flatMap((page) => page.rows), query };
}

// ---------------------------------------------------------------- the dashboard

export function useDashboard(enabled = true) {
  const { api, slug } = useScope();
  return useQuery({ queryKey: [slug, ROOT, "dashboard"], enabled, staleTime: 60_000, queryFn: ({ signal }) => api.reports.getDashboard(signal) });
}

// ---------------------------------------------------------------- saved reports

export function useSavedList(filters: { q?: string | undefined; mine?: boolean | undefined; dataset?: string | undefined }) {
  const { api, slug } = useScope();
  return useInfiniteQuery({
    queryKey: [slug, ROOT, "saved", "list", filters],
    ...cursorPaging<Awaited<ReturnType<typeof api.reports.listSaved>>["items"][number]>(({ cursor, signal }) => api.reports.listSaved({ cursor, signal, limit: 25, ...filters })),
  });
}

export function useSaved(id: string | undefined) {
  const { api, slug } = useScope();
  return useQuery({ queryKey: [slug, ROOT, "saved", "one", id], enabled: Boolean(id), queryFn: ({ signal }) => api.reports.getSaved(id ?? "", signal) });
}

export function useCreateSaved() {
  const { api, queryClient, slug } = useScope();
  return useMutation({ mutationFn: (input: In<"reports_create_saved">) => api.reports.createSaved(input), onSuccess: () => refreshSaved(queryClient, slug) });
}

export function useUpdateSaved() {
  const { api, queryClient, slug } = useScope();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: In<"reports_update_saved"> }) => api.reports.updateSaved(id, patch),
    onSuccess: (saved) => {
      queryClient.setQueryData([slug, ROOT, "saved", "one", saved.id], saved);
      refreshSaved(queryClient, slug);
      void queryClient.invalidateQueries({ queryKey: [slug, ROOT, "run"] });
    },
  });
}

export function useDeleteSaved() {
  const { api, queryClient, slug } = useScope();
  return useMutation({ mutationFn: (id: string) => api.reports.deleteSaved(id), onSuccess: () => refreshSaved(queryClient, slug) });
}

export function useDuplicateSaved() {
  const { api, queryClient, slug } = useScope();
  return useMutation({ mutationFn: (id: string) => api.reports.duplicateSaved(id), onSuccess: () => refreshSaved(queryClient, slug) });
}

/** The club's roles to share with. Only people who may share can read them. */
export function useShareRoles(enabled: boolean) {
  const { api, slug } = useScope();
  return useQuery({ queryKey: [slug, ROOT, "share-roles"], enabled, staleTime: 120_000, queryFn: ({ signal }) => api.reports.listShareRoles(signal) });
}

// ---------------------------------------------------------------- schedules

export function useSchedules(reportId: string, enabled: boolean) {
  const { api, slug } = useScope();
  return useQuery({ queryKey: [slug, ROOT, "schedules", reportId], enabled, queryFn: ({ signal }) => api.reports.listSchedules(reportId, signal) });
}

function useRefreshSchedules(reportId: string) {
  const { queryClient, slug } = useScope();
  return () => {
    void queryClient.invalidateQueries({ queryKey: [slug, ROOT, "schedules", reportId] });
    refreshSaved(queryClient, slug); // the library shows how many schedules a report has
  };
}

export function useCreateSchedule(reportId: string) {
  const { api } = useScope();
  const refresh = useRefreshSchedules(reportId);
  return useMutation({ mutationFn: (input: ReportScheduleInput) => api.reports.createSchedule(reportId, input), onSuccess: refresh });
}

export function useUpdateSchedule(reportId: string) {
  const { api } = useScope();
  const refresh = useRefreshSchedules(reportId);
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: In<"reports_update_schedule"> }) => api.reports.updateSchedule(id, patch),
    onSuccess: refresh,
  });
}

export function useDeleteSchedule(reportId: string) {
  const { api } = useScope();
  const refresh = useRefreshSchedules(reportId);
  return useMutation({ mutationFn: (id: string) => api.reports.deleteSchedule(id), onSuccess: refresh });
}

// ---------------------------------------------------------------- export

export function useExportReport() {
  const { api } = useScope();
  return useMutation({ mutationFn: (source: RunSource) => api.reports.exportReport({ ...source }) });
}
